/**
 * API client for backend communication.
 * All requests go through this client to ensure consistent error handling.
 */

// API_BASE should be the full base URL including /api
// VITE_API_BASE_URL should be just the domain: https://api.seo.omensystems.com
// In development: /api (proxied by Vite)
function getApiBase(): string {
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  if (!envUrl) return '/api';

  // Remove trailing slash if present
  const baseUrl = envUrl.replace(/\/+$/, '');

  // If env var already ends with /api, use as-is
  if (baseUrl.endsWith('/api')) {
    return baseUrl;
  }

  // Otherwise append /api
  return `${baseUrl}/api`;
}

const API_BASE = getApiBase();

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface ApiResponse<T> {
  data?: T;
  error?: ApiError;
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE}${endpoint}`;

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        error: data.error || {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred',
        },
      };
    }

    return { data };
  } catch (err) {
    return {
      error: {
        code: 'NETWORK_ERROR',
        message: 'Unable to connect to server',
      },
    };
  }
}

import type { ScanResponse, SubscriptionInfo } from '@wrm/shared';

export const api = {
  get: <T>(endpoint: string, token?: string) =>
    request<T>(endpoint, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }),

  post: <T>(endpoint: string, body: unknown, token?: string) =>
    request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }),

  delete: <T>(endpoint: string, token?: string) =>
    request<T>(endpoint, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }),

  // Anonymous scan endpoints
  async startAnonymousScan(url: string): Promise<{ scanId: string }> {
    const response = await request<{ scanId: string }>('/scan/anonymous', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
    if (response.error) {
      throw new Error(response.error.message);
    }
    return response.data!;
  },

  async getAnonymousScanStatus(scanId: string): Promise<ScanResponse> {
    const response = await request<ScanResponse>(`/scan/anonymous/${scanId}`);
    if (response.error) {
      throw new Error(response.error.message);
    }
    return response.data!;
  },

  // Authenticated scan endpoints
  async startScan(url: string, token: string, websiteId?: string): Promise<{ scanId: string; status: string; results?: unknown; aiRecommendations?: unknown }> {
    const response = await request<{ scanId: string; status: string; results?: unknown; aiRecommendations?: unknown }>('/scan', {
      method: 'POST',
      body: JSON.stringify({ url, websiteId }),
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.error) {
      throw new Error(response.error.message);
    }
    return response.data!;
  },

  async getScanStatus(scanId: string, token: string): Promise<ScanResponse> {
    const response = await request<ScanResponse>(`/scan/${scanId}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.error) {
      throw new Error(response.error.message);
    }
    return response.data!;
  },

  async getLatestScanForWebsite(websiteId: string, token: string): Promise<{ scanId: string; status: string; results?: unknown; aiRecommendations?: unknown } | null> {
    const response = await request<{ scanId: string; status: string; results?: unknown; aiRecommendations?: unknown }>(`/scan/website/${websiteId}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.error) {
      // 404 means no scan found - return null instead of throwing
      if (response.error.code === 'NOT_FOUND') {
        return null;
      }
      throw new Error(response.error.message);
    }
    return response.data!;
  },

  // Website management
  async getWebsites(token: string): Promise<{ websites: Array<{ id: string; url: string; currentScore: number | null; lastScanAt: string | null }> }> {
    const response = await request<{ websites: Array<{ id: string; url: string; currentScore: number | null; lastScanAt: string | null }> }>('/websites', {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.error) {
      throw new Error(response.error.message);
    }
    return response.data!;
  },

  async addWebsite(url: string, token: string): Promise<{ websiteId: string }> {
    const response = await request<{ websiteId: string }>('/websites', {
      method: 'POST',
      body: JSON.stringify({ url }),
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.error) {
      throw new Error(response.error.message);
    }
    return response.data!;
  },

  async removeWebsite(websiteId: string, token: string): Promise<void> {
    const response = await request<void>(`/websites/${websiteId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.error) {
      throw new Error(response.error.message);
    }
  },

  // Subscription
  async getSubscriptionStatus(token: string): Promise<SubscriptionInfo> {
    const response = await request<SubscriptionInfo>('/subscription/status', {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.error) {
      throw new Error(response.error.message);
    }
    return response.data!;
  },

  async createCheckoutSession(tier: string, token: string): Promise<{ checkoutUrl: string }> {
    const response = await request<{ checkoutUrl: string }>('/subscription/checkout', {
      method: 'POST',
      body: JSON.stringify({ tier }),
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.error) {
      throw new Error(response.error.message);
    }
    return response.data!;
  },

  async cancelSubscription(token: string): Promise<void> {
    const response = await request<void>('/subscription/cancel', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.error) {
      throw new Error(response.error.message);
    }
  },

  // Reports
  async generateReport(scanId: string, token: string): Promise<{ reportUrl: string }> {
    const response = await request<{ downloadUrl: string }>(`/report/${scanId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.error) {
      throw new Error(response.error.message);
    }
    // Backend returns downloadUrl, frontend expects reportUrl
    return { reportUrl: response.data!.downloadUrl };
  },
};
